import {useEffect, useRef, useState} from 'react'

export interface UseDragDropOptions {
  onFilesDrop: (files: FileList) => void
  targetElement?: EventTarget | null
}

export function useDragDrop({onFilesDrop, targetElement}: UseDragDropOptions) {
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  useEffect(() => {
    if (!targetElement) {
      return
    }

    const handleDragOver = (event: Event) => {
      event.preventDefault()
      event.stopPropagation()
    }

    const handleDragEnter = (event: Event) => {
      if (!isDragEvent(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      // Only handle file drag-and-drops.
      if (event.dataTransfer?.types.includes('Files')) {
        dragCounterRef.current++
        if (dragCounterRef.current === 1) {
          setIsDragging(true)
        }
      }
    }

    const handleDragLeave = (event: Event) => {
      event.preventDefault()
      event.stopPropagation()

      dragCounterRef.current--
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0
        setIsDragging(false)
      }
    }

    const handleDrop = (event: Event) => {
      if (!isDragEvent(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      dragCounterRef.current = 0
      setIsDragging(false)

      const files = event.dataTransfer?.files
      if (!files || files.length === 0) return

      onFilesDrop(files)
    }

    targetElement.addEventListener('dragover', handleDragOver)
    targetElement.addEventListener('dragenter', handleDragEnter)
    targetElement.addEventListener('dragleave', handleDragLeave)
    targetElement.addEventListener('drop', handleDrop)

    return () => {
      targetElement.removeEventListener('dragover', handleDragOver)
      targetElement.removeEventListener('dragenter', handleDragEnter)
      targetElement.removeEventListener('dragleave', handleDragLeave)
      targetElement.removeEventListener('drop', handleDrop)
    }
  }, [onFilesDrop, targetElement])

  return {isDragging}
}

function isDragEvent(event: Event): event is DragEvent {
  return event instanceof DragEvent
}
